import {
  countries,
  countryMatchesSearch,
  findCountryName,
} from "@redotech/locale/countries";
import { shopifyProvinces } from "@redotech/locale/provinces";
import { formatCountryColumn } from "@redotech/redo-fulfillment-rate-table-common/rate-table";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { memo, useMemo, useState } from "react";
import { countryColumnLabel, provinceName } from "./country-column-label";
import { RateTableColumnHeader } from "./rate-table";
import { RateCardForm } from "./rate-table-utils";

/** Country-column state and grid mutations shared by rate card editors. */
export function useCountryColumns({
  input,
  initialColumns,
  includeRestOfWorld,
}: {
  input: RateCardForm;
  initialColumns: string[] | (() => string[]);
  /** Whether the grid rows end with a pinned rest-of-world cell. */
  includeRestOfWorld: boolean;
}) {
  const [countryColumns, setCountryColumns] =
    useState<string[]>(initialColumns);
  const [pendingCountry, setPendingCountry] = useState<string | undefined>(
    undefined,
  );
  const [pendingProvince, setPendingProvince] = useState<string | undefined>(
    undefined,
  );
  const [countrySearch, setCountrySearch] = useState("");

  const columnHeaders = useMemo<RateTableColumnHeader[]>(
    () => [
      ...countryColumns.map((column) => ({
        label: countryColumnLabel(column),
        deletable: true,
      })),
      ...(includeRestOfWorld
        ? [{ label: "Rest of world", deletable: false }]
        : []),
    ],
    [countryColumns, includeRestOfWorld],
  );

  const provinceOptions = useMemo(
    () =>
      pendingCountry
        ? shopifyProvinces.filter((p) => p.countryCode === pendingCountry)
        : [],
    [pendingCountry],
  );

  const addCountryColumn = () => {
    if (!pendingCountry) {
      return;
    }
    const column = formatCountryColumn(pendingCountry, pendingProvince);
    if (countryColumns.includes(column)) {
      setPendingCountry(undefined);
      setPendingProvince(undefined);
      return;
    }
    setCountryColumns([...countryColumns, column]);
    input.inputs.data.setValue(
      input.value.data.map((row) => {
        // Insert before the pinned rest-of-world cell when it's present
        const insertAt = includeRestOfWorld
          ? Math.max(row.length - 1, 1)
          : row.length;
        return [...row.slice(0, insertAt), "0", ...row.slice(insertAt)];
      }),
    );
    setPendingCountry(undefined);
    setPendingProvince(undefined);
  };

  const deleteCountryColumn = (columnIndex: number) => {
    setCountryColumns(countryColumns.filter((_, i) => i !== columnIndex - 1));
    input.inputs.data.setValue(
      input.value.data.map((row) => row.filter((_, i) => i !== columnIndex)),
    );
  };

  return {
    countryColumns,
    columnHeaders,
    pendingCountry,
    setPendingCountry,
    pendingProvince,
    setPendingProvince,
    countrySearch,
    setCountrySearch,
    provinceOptions,
    addCountryColumn,
    deleteCountryColumn,
  };
}

export type CountryColumns = ReturnType<typeof useCountryColumns>;

/** Country + province pickers with an add button, driven by useCountryColumns. */
export const CountryColumnPicker = memo(function CountryColumnPicker({
  columns,
  disabled = false,
  dropdownClassName,
  dropdownSize = "sm",
  buttonSize = "lg",
}: {
  columns: CountryColumns;
  disabled?: boolean;
  dropdownClassName?: string;
  dropdownSize?: "xs" | "sm";
  buttonSize?: "sm" | "lg";
}) {
  return (
    <>
      <RedoSingleSelectDropdownInput<string>
        className={dropdownClassName}
        label="Country"
        options={countries
          .filter((c) =>
            countryMatchesSearch(c.code, columns.countrySearch, c.name),
          )
          .slice(0, 50)
          .map((c) => ({
            id: c.code,
            type: "text" as const,
            text: c.name,
            value: c.code,
          }))}
        optionSelected={({ item }) => {
          columns.setPendingCountry(item.value);
          columns.setPendingProvince(undefined);
        }}
        placeholder="Add country"
        searchString={columns.countrySearch}
        selectedItem={
          columns.pendingCountry
            ? {
                id: columns.pendingCountry,
                type: "text",
                text:
                  findCountryName(columns.pendingCountry) ??
                  columns.pendingCountry,
                value: columns.pendingCountry,
              }
            : undefined
        }
        setSearchString={columns.setCountrySearch}
        size={dropdownSize}
      />
      {columns.provinceOptions.length > 0 && (
        <RedoSingleSelectDropdownInput<string>
          className={dropdownClassName}
          label="Province"
          options={[
            {
              id: "__all__",
              type: "text" as const,
              text: "All provinces",
              value: "",
            },
            ...columns.provinceOptions.map((p) => ({
              id: p.provinceCode,
              type: "text" as const,
              text: p.province,
              value: p.provinceCode,
            })),
          ]}
          optionSelected={({ item }) => {
            columns.setPendingProvince(item.value || undefined);
          }}
          selectedItem={{
            id: columns.pendingProvince ?? "__all__",
            type: "text",
            text: columns.pendingProvince
              ? provinceName(
                  columns.pendingCountry ?? "",
                  columns.pendingProvince,
                )
              : "All provinces",
            value: columns.pendingProvince ?? "",
          }}
          size={dropdownSize}
        />
      )}
      <RedoButton
        disabled={disabled || !columns.pendingCountry}
        hierarchy="tertiary"
        IconLeading={PlusIcon}
        onClick={columns.addCountryColumn}
        size={buttonSize}
        text="Add country"
        theme="normal"
      />
    </>
  );
});
